import { getUsers } from '../../../api/services/userService';
import { impersonate } from '../../../slices/userSlice';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Notification } from '../../../components/notifications';
import { PencilIcon } from '@heroicons/react/solid';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useAsync } from 'react-async';
import DataTable from '../../../components/dataTable';
import Layout from '../../../components/layout';
import LayoutError from '../../../components/layoutError';
import React, { Fragment, useState } from 'react';
import User from '../../../api/models/user';
import UserRole from '../../../api/models/userrole';
import { Role } from '../../../common/constants';
import { clearLocalStorage } from '../../../common/utils';

type ViewState = {
  message?: string;
};

const fetchUsersInitial = async () => {
  return await getUsers('', '', '');
};

const ViewUsers = () => {
  const currentUser = useAppSelector((s) => s.user);
  const isSuperUser = currentUser.roles?.includes(Role.SuperUser);

  const columns = React.useMemo(
    () => [
      {
        Header: 'First Name',
        accessor: 'firstName',
        width: 200,
        className: "hidden md:table-cell"
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
        width: 200,
        className: "hidden md:table-cell"
      },
      {
        Header: 'Email',
        accessor: 'emailAddress',
        width: 250,
      },
      {
        id: 'userRoles',
        Header: 'Roles',
        accessor: (u: any) => u.userRoles.length === 0 ? '-' : u.userRoles.sort((a: any, b: any) => (a.role.name.toLowerCase() > b.role.name.toLowerCase()) ? 1 : -1).map((d: any) => d.role.name).join(', '),
        width: 460,
      },
      {
        id: 'isVisible',
        Header: 'Active',
        accessor: (u: any) => (u.isDisabled ? 'No' : 'Yes'),
        width: 84,
        className: "hidden sm:table-cell"
      },
      {
        id: 'actions',
        disableSortBy: true,
        Header: '',
        accessor: (u: any) => {
          return currentUser.roles?.includes(Role.Operations) ? <></> : (
            <div>
              <div className='flex items-center justify-between'>
                <button
                  disabled={u.userId === currentUser.id || (!isSuperUser && u.userRoles.map((e: UserRole) => e.role.name).includes(Role.Admin || Role.SuperUser))}
                  className='px-2 py-1 secondary'
                  onClick={(e) => { e.stopPropagation(); changeToImpersonatedUser(u.userId ?? -1); }}
                >
                  Impersonate
                </button>

                <div className='clearfix'>
                  <span className='float-right'>
                    <PencilIcon className="w-5 h-5" aria-hidden="true" />
                  </span>
                </div>
              </div>
            </div>
          );
        },
        width: 200,
        className: "hidden sm:table-cell"
      },
    ],
    []
  );

  const dispatch = useAppDispatch();
  const history = useHistory();
  const { state } = useLocation<ViewState>();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  const { error, isPending } = useAsync({
    promiseFn: fetchUsersInitial,
    onResolve: (r) => {
      setUsers(r.data.users);
      setFilteredUsers(r.data.users);
    },
  });

  const changeToImpersonatedUser = async (userId: number) => {
    if (!userId) {
      return Promise.reject('User Id missing. Unable to impersonate.');
    }
    try {
      clearLocalStorage();
      dispatch(impersonate(userId));
    }
    catch (err) {
      return Promise.reject('Failed to impersonate user.');
    }
  }

  if (error) return <LayoutError />;

  return (
    <Layout isLoading={isPending}>
      <Fragment>
        {state?.message && <Notification message={state?.message} />}

        <div className='mb-4 text-right'>
          <Link className='btn' to='/admin/users/new'>
            Add User
          </Link>
        </div>

        <div>
          <label className="w-full">
            <label className="block mb-1">Filter:</label>
            <input
              type="text"
              placeholder={`${filteredUsers.length} records...`}
              autoComplete="off"
              data-lpignore="true"
              className=""
              onChange={(e) => {
                setFilteredUsers(current => users.filter(user => `${user.firstName} ${user.lastName}`.toLowerCase().includes(e.currentTarget.value.toLowerCase())));
              }}
            />
          </label>
        </div>
        <DataTable
          columns={columns}
          data={filteredUsers}
          skipFilter={true}
          showPagination={true}
          getRowProps={(row: any) => ({
            onClick: () => {
              history.push(`/admin/users/${row.original.userId}`);
            },
            className: "cursor-pointer hover:bg-gray-100",
          })}
        />
      </Fragment>
    </Layout>
  );
};

export default ViewUsers;
